import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type RefObject,
} from "react";
import Check from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import ErrorOutline from "@mui/icons-material/ErrorOutline";
import {
  Autocomplete,
  Box,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Fade,
  FormControl,
  FormControlLabel,
  FormGroup,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Radio,
  RadioGroup,
  Select,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";
import { useDebouncedEffect } from "@wojtekmaj/react-hooks";
import { debounce } from "lodash-es";
import { useNavigate } from "react-router-dom";
import { z } from "zod";

import type { DateRangeUnbranded, TimeRangeOnWeekday } from "@thalamos/common";
import { dayjs, ISO_DATE_FORMAT } from "@thalamos/common";
import type {
  Section12AvailabilityOverrideDate,
  Section12ExpertiseLevel,
  Section12Gender,
  Section12Language,
  Section12LocalAuthorityMapping,
  Section12Specialism,
  SetSection12LocalAuthorityMappingsRequest,
} from "@thalamos/aspire-common";
import {
  isSection12Accreditation,
  SECTION_12_EXPERTISE_LEVEL_LABELS,
  SECTION_12_GENDER_LABELS,
  Section12AvailabilityWeekdayBlockInputSchema,
  type ExtendedThalamosUser,
} from "@thalamos/aspire-common";

import { api, apiHooks } from "../../api.js";
import { config } from "../../config.js";
import {
  LoggedInUserContext,
  ManagedLocationsContext,
} from "../../Contexts.js";
import { routeFns } from "../../routePaths.js";
import type { AutoSaveStatus } from "./autoSave.js";
import { AUTO_SAVE_DELAY, SAVED_STATUS_DURATION } from "./autoSave.js";
import { CalendarSyncSection } from "./CalendarSyncSection.js";
import type { CalendarSyncSummary } from "./CalendarSyncSection.js";
import { DefaultWorkingHoursSection } from "./DefaultWorkingHoursSection.js";
import { PhoneVerificationSection } from "./PhoneVerificationSection.js";
import { buildSetupAreas, SetupScoreCards } from "./SetupScoreCards.js";
import {
  UPCOMING_WEEKS_AHEAD,
  UpcomingAvailabilityCalendar,
  WEEK_STARTS_ON,
} from "./UpcomingAvailabilityCalendar.js";
import {
  AddressField,
  Banner,
  BannerList,
  Button,
  ButtonIcon,
  LoadingSpinner,
  renderErrorToast,
  startOfWeekContaining,
  Typeahead,
} from "~/components/design-system/index.js";
import {
  TrustPicker,
  type SelectedTrust,
} from "~/components/form/TrustPicker.js";
import { Container } from "~/components/layout/styleWrappers.js";

interface Section12ProfileFormState {
  selectedLocalAuthorities: Section12LocalAuthorityMapping[];
  selectedSpecialismIds: string[];
  defaultWeek: TimeRangeOnWeekday[];
}

/**
 * Brand boundary for the repeating week: wire values are parsed here so the
 * rest of the page works in domain types.
 */
const DefaultWeekSchema = z.array(Section12AvailabilityWeekdayBlockInputSchema);

function buildInitialState(
  localAuthorityMappings: Section12LocalAuthorityMapping[],
  specialisms: Section12Specialism[],
  availabilityBlocks: ExtendedThalamosUser["section12AvailabilityBlocks"],
): Section12ProfileFormState {
  return {
    selectedLocalAuthorities: [...localAuthorityMappings],
    selectedSpecialismIds: specialisms.map((s) => s.id),
    defaultWeek: DefaultWeekSchema.parse(availabilityBlocks),
  };
}

/**
 * The badge shows the least settled of the sections that save on their own, so
 * one section finishing cannot announce "Saved" while another is still running.
 */
function mergeAutoSaveStatus(
  left: AutoSaveStatus,
  right: AutoSaveStatus,
): AutoSaveStatus {
  if (left === "saving" || right === "saving") {
    return "saving";
  }
  if (left === "error" || right === "error") {
    return "error";
  }
  if (left === "saved" || right === "saved") {
    return "saved";
  }
  return "idle";
}

export function Section12ProfileInner({
  userId,
  refetchUser,
  embedded = false,
  externalActiveSection,
  onActiveSectionChange,
  onAutoSave,
  viewedUser,
  saveNowRef,
}: {
  userId: string;
  refetchUser: () => void;
  embedded?: boolean;
  externalActiveSection?: string;
  onActiveSectionChange?: (section: string) => void;
  onAutoSave?: (status: "saving" | "saved" | "error") => void;
  /** When provided, used for initial form state instead of the logged-in user context. */
  viewedUser?: ExtendedThalamosUser | null;
  /** When provided, the component populates this ref with a function that
   *  flushes any pending debounced auto-save immediately. */
  saveNowRef?: RefObject<(() => Promise<void>) | null>;
}) {
  const navigate = useNavigate();
  const userContext = useContext(LoggedInUserContext);
  const managedLocationsContext = useContext(ManagedLocationsContext);

  // Use the explicitly-provided viewed user when available, otherwise fall
  // back to the logged-in user context (self-editing scenario).
  const user = viewedUser ?? userContext?.user;
  const contextRefetchUser = userContext?.refetchUser;

  // When embedded inside UserProfile the `refetchUser` prop refreshes a
  // *different* API call (`/api/user/:id`) than the global context which
  // uses `/api/user/me`. We must refresh both so that remounting this
  // component after navigation reads up-to-date data from the context.
  const combinedRefetch = useCallback(() => {
    refetchUser();
    if (contextRefetchUser && contextRefetchUser !== refetchUser) {
      contextRefetchUser();
    }
  }, [refetchUser, contextRefetchUser]);

  // Get all available specialisms
  const [{ data: allSpecialisms, loading: specialismsLoading }] =
    apiHooks.section12.getAllSpecialisms();
  const [{ data: allLanguages, loading: languagesLoading }] =
    apiHooks.section12.getAllLanguages();

  // Form state
  const [formState, setFormState] = useState<Section12ProfileFormState>(() =>
    buildInitialState(
      user?.section12LocalAuthorityMappings ?? [],
      user?.section12Specialisms ?? [],
      user?.section12AvailabilityBlocks ?? [],
    ),
  );

  const [laInputValue, setLaInputValue] = useState("");
  const [specialismInputValue, setSpecialismInputValue] = useState("");

  // Home base location state
  const [homeBaseAddress, setHomeBaseAddress] = useState(
    user?.homeBaseAddress?.address ?? "",
  );
  const [homeBasePostalCode, setHomeBasePostalCode] = useState(
    user?.homeBaseAddress?.postalCode ?? "",
  );

  // Gender state
  const [selectedGender, setSelectedGender] = useState<Section12Gender | "">(
    user?.section12Gender ?? "",
  );

  // Languages state
  const [selectedLanguageIds, setSelectedLanguageIds] = useState<string[]>(
    user?.section12Languages?.map((language) => language.id) ?? [],
  );

  // Trust affiliation state
  const [worksForTrust, setWorksForTrust] = useState<boolean>(
    user?.section12WorksForTrust ?? false,
  );
  const notListedTrust = managedLocationsContext.trusts.find(
    (t) => t.notListed,
  );
  const [selectedTrust, setSelectedTrust] = useState<SelectedTrust>(() => {
    if (user?.section12TrustId) {
      const isNotListed = user.section12TrustId === notListedTrust?.id;
      return {
        id: user.section12TrustId,
        name: user.section12TrustName ?? "",
        isNotListed,
      };
    }
    if (user?.section12TrustName) {
      return {
        id: undefined,
        name: user.section12TrustName,
        isNotListed: false,
      };
    }
    return null;
  });
  const [trustTeam, setTrustTeam] = useState(user?.section12TrustTeam ?? "");

  // Max travel time state
  const [maxTravelHours, setMaxTravelHours] = useState<string>(() => {
    const mins = user?.section12MaxTravelTimeMinutes;
    return mins != null ? String(Math.floor(mins / 60)) : "";
  });
  const [maxTravelMinutes, setMaxTravelMinutes] = useState<string>(() => {
    const mins = user?.section12MaxTravelTimeMinutes;
    return mins != null ? String(mins % 60) : "";
  });

  // Contact preferences state
  const [contactPreferenceEmail, setContactPreferenceEmail] = useState(
    user?.section12ContactPreferenceEmail ?? true,
  );
  const [contactPreferenceText, setContactPreferenceText] = useState(
    user?.section12ContactPreferenceText ?? true,
  );

  // Expertise levels per specialism
  const [specialismExpertiseLevels, setSpecialismExpertiseLevels] = useState<
    Record<string, Section12ExpertiseLevel | null>
  >(() => {
    const levels: Record<string, Section12ExpertiseLevel | null> = {};
    for (const s of user?.section12Specialisms ?? []) {
      levels[s.id] = (s.expertiseLevel as Section12ExpertiseLevel) ?? null;
    }
    return levels;
  });

  // Auto-save status
  const [autoSaveStatus, setAutoSaveStatus] = useState<AutoSaveStatus>("idle");
  // The upcoming calendar saves on its own schedule, so it reports separately.
  const [calendarSaveStatus, setCalendarSaveStatus] =
    useState<AutoSaveStatus>("idle");
  const displayedAutoSaveStatus = mergeAutoSaveStatus(
    autoSaveStatus,
    calendarSaveStatus,
  );
  // "Saved" is a moment rather than a state, so it clears itself after a pause.
  useDebouncedEffect(
    () => {
      if (autoSaveStatus === "saved") {
        setAutoSaveStatus("idle");
      }
    },
    [autoSaveStatus],
    SAVED_STATUS_DURATION,
  );
  useDebouncedEffect(
    () => {
      if (calendarSaveStatus === "saved") {
        setCalendarSaveStatus("idle");
      }
    },
    [calendarSaveStatus],
    SAVED_STATUS_DURATION,
  );
  const initialLoadRef = useRef(true);

  // Filter local authorities that can be selected
  const localAuthorities = managedLocationsContext.managedLocations
    .filter((location) => location.type === "localAuthority")
    .sort((a, b) => a.name.localeCompare(b.name));

  // Filter out already selected LAs
  const availableLocalAuthorities = localAuthorities.filter(
    (la) =>
      !formState.selectedLocalAuthorities.some(
        (selected) => selected.locationId === la.id,
      ),
  );

  // Filter out already selected specialisms
  const availableSpecialisms = (allSpecialisms ?? []).filter(
    (s) => !formState.selectedSpecialismIds.includes(s.id),
  );

  const selectedLanguages = useMemo(
    () =>
      selectedLanguageIds
        .map((id) => allLanguages?.find((language) => language.id === id))
        .filter((language): language is Section12Language => !!language),
    [allLanguages, selectedLanguageIds],
  );

  // Handle adding a local authority
  const handleAddLocalAuthority = (la: { id: string; name: string }) => {
    setFormState((prev) => ({
      ...prev,
      selectedLocalAuthorities: [
        ...prev.selectedLocalAuthorities,
        { id: crypto.randomUUID(), locationId: la.id, locationName: la.name },
      ],
    }));
    setLaInputValue("");
  };

  // Handle removing a local authority
  const handleRemoveLocalAuthority = (locationId: string) => {
    setFormState((prev) => ({
      ...prev,
      selectedLocalAuthorities: prev.selectedLocalAuthorities.filter(
        (la) => la.locationId !== locationId,
      ),
    }));
  };

  // Handle adding a specialism
  const handleAddSpecialism = (specialism: Section12Specialism) => {
    setFormState((prev) => ({
      ...prev,
      selectedSpecialismIds: [...prev.selectedSpecialismIds, specialism.id],
    }));
    setSpecialismInputValue("");
  };

  // Handle removing a specialism
  const handleRemoveSpecialism = (specialismId: string) => {
    setFormState((prev) => ({
      ...prev,
      selectedSpecialismIds: prev.selectedSpecialismIds.filter(
        (id) => id !== specialismId,
      ),
    }));
  };

  // Overrides for the weeks the upcoming calendar can reach. Held here because
  // both the calendar and a re-sync of the doctor's default week change them.
  const [overrides, setOverrides] = useState<
    Section12AvailabilityOverrideDate[]
  >([]);

  // Frozen for the lifetime of the page: the horizon and the shading of time
  // already past must not shift under an edit in progress.
  const now = useMemo(() => dayjs(), []);

  const overrideRange = useMemo<DateRangeUnbranded>(() => {
    const firstWeek = startOfWeekContaining(now, WEEK_STARTS_ON);
    return {
      from: firstWeek.format(ISO_DATE_FORMAT),
      to: firstWeek
        .add(UPCOMING_WEEKS_AHEAD, "week")
        .subtract(1, "day")
        .format(ISO_DATE_FORMAT),
    };
  }, [now]);

  const fetchOverrides = useCallback(async (): Promise<void> => {
    try {
      const response = await api.section12.getUserAvailabilityOverrides(
        userId,
        overrideRange,
      );
      setOverrides(response.data.overrides);
    } catch {
      renderErrorToast({ message: "Failed to load availability overrides" });
    }
  }, [userId, overrideRange]);

  useEffect(() => {
    void fetchOverrides();
  }, [fetchOverrides]);

  const calendarFlushRef = useRef<(() => Promise<void>) | null>(null);

  // ── Auto-save helpers ──────────────────────────────────────────────

  const markSaved = useCallback(() => {
    setAutoSaveStatus("saved");
    onAutoSave?.("saved");
  }, [onAutoSave]);

  // The calendar reports what happened; how long "Saved" lingers is the page's
  // call, the same as for its own sections.
  const handleCalendarAutoSaveStatus = useCallback(
    (status: AutoSaveStatus) => {
      setCalendarSaveStatus(status);
      if (status !== "idle") {
        onAutoSave?.(status);
      }
    },
    [onAutoSave],
  );

  const saveWorkingHours = useCallback(
    async (defaultWeek: TimeRangeOnWeekday[], syncActive: boolean) => {
      // Everything below replaces what the calendar is showing — the refetch
      // of the user, and a re-sync rewriting the imported layer under it — so
      // an override still being painted has to land first or it is lost.
      await calendarFlushRef.current?.();

      await api.section12.setUserAvailability(userId, {
        blocks: defaultWeek.map((range) => ({
          dayOfWeek: range.dayOfWeek,
          from: range.from,
          until: range.until,
        })),
      });
      combinedRefetch();
      if (syncActive) {
        try {
          await api.section12.triggerCalendarSyncAll(userId);
          await fetchOverrides();
        } catch {
          // The hours themselves are saved; the imported layer is merely stale
          // until the next sync, which is not worth alarming the doctor over.
        }
      }
    },
    [userId, combinedRefetch, fetchOverrides],
  );

  const saveLACoverage = useCallback(
    async (las: Section12LocalAuthorityMapping[]) => {
      const laMappingsRequest: SetSection12LocalAuthorityMappingsRequest = {
        mappings: las.map((la) => ({
          locationId: la.locationId,
          locationName: la.locationName,
        })),
      };
      await api.section12.setUserLocalAuthorityMappings(
        userId,
        laMappingsRequest,
      );
      combinedRefetch();
    },
    [userId, combinedRefetch],
  );

  const saveHomeBase = useCallback(
    async (address: string, postalCode: string) => {
      await api.section12.setHomeBaseLocation(userId, {
        address: address || null,
        postalCode: postalCode || null,
      });
      combinedRefetch();
    },
    [userId, combinedRefetch],
  );

  const saveGender = useCallback(
    async (gender: Section12Gender | "") => {
      await api.section12.setUserGender(userId, {
        gender: gender || null,
      });
      combinedRefetch();
    },
    [userId, combinedRefetch],
  );

  const saveLanguages = useCallback(
    async (languageIds: string[]) => {
      await api.section12.setUserLanguages(userId, { languageIds });
      combinedRefetch();
    },
    [userId, combinedRefetch],
  );

  const saveTrustAffiliation = useCallback(
    async (works: boolean, trust: SelectedTrust, team: string) => {
      await api.section12.setUserTrustAffiliation(userId, {
        worksForTrust: works,
        trustId: works ? (trust?.id ?? null) : null,
        trustName: works ? (trust?.name ?? null) : null,
        trustTeam: works ? team || null : null,
      });
      combinedRefetch();
    },
    [userId, combinedRefetch],
  );

  const saveMaxTravelTime = useCallback(
    async (hours: string, minutes: string) => {
      const h = parseInt(hours, 10) || 0;
      const m = parseInt(minutes, 10) || 0;
      const total = Math.min(h * 60 + m, 1440);
      await api.section12.setUserMaxTravelTime(userId, {
        maxTravelTimeMinutes: total > 0 ? total : null,
      });
      combinedRefetch();
    },
    [userId, combinedRefetch],
  );

  const saveContactPreferences = useCallback(
    async (email: boolean, text: boolean) => {
      await api.section12.setUserContactPreferences(userId, {
        email,
        text,
      });
      combinedRefetch();
    },
    [userId, combinedRefetch],
  );

  const saveSpecialismsWithLevels = useCallback(
    async (
      ids: string[],
      levels: Record<string, Section12ExpertiseLevel | null>,
    ) => {
      await api.section12.setUserSpecialismsWithLevels(userId, {
        specialisms: ids.map((id) => ({
          specialismId: id,
          expertiseLevel: levels[id] ?? null,
        })),
      });
      combinedRefetch();
    },
    [userId, combinedRefetch],
  );

  // Serialise the default week for stable dep comparison
  const defaultWeekKey = useMemo(
    () =>
      formState.defaultWeek
        .map(
          (range) => `${String(range.dayOfWeek)}:${range.from}-${range.until}`,
        )
        .sort()
        .join(","),
    [formState.defaultWeek],
  );
  const lasKey = useMemo(
    () =>
      formState.selectedLocalAuthorities
        .map((la) => la.locationId)
        .sort()
        .join(","),
    [formState.selectedLocalAuthorities],
  );
  const specialismsKey = useMemo(
    () => [...formState.selectedSpecialismIds].sort().join(","),
    [formState.selectedSpecialismIds],
  );

  // Track the last-persisted snapshot so we only save real changes
  const lastPersistedRef = useRef({
    defaultWeekKey,
    lasKey,
    specialismsKey,
    homeBaseAddress,
    homeBasePostalCode,
    selectedGender,
    selectedLanguagesKey: [...selectedLanguageIds].sort().join(","),
    worksForTrust,
    trustId: selectedTrust?.id ?? "",
    trustName: selectedTrust?.name ?? "",
    trustTeam,
    maxTravelHours,
    maxTravelMinutes,
    contactPreferenceEmail,
    contactPreferenceText,
    specialismExpertiseLevelsKey: JSON.stringify(specialismExpertiseLevels),
  });

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedAutoSave = useCallback(
    debounce(
      async (
        defaultWeek: TimeRangeOnWeekday[],
        las: Section12LocalAuthorityMapping[],
        specIds: string[],
        address: string,
        postalCode: string,
        syncActive: boolean,
        newDefaultWeekKey: string,
        newLasKey: string,
        newSpecKey: string,
        gender: Section12Gender | "",
        languageIds: string[],
        works: boolean,
        trust: SelectedTrust,
        team: string,
        travelH: string,
        travelM: string,
        emailPref: boolean,
        textPref: boolean,
        expertiseLevels: Record<string, Section12ExpertiseLevel | null>,
      ) => {
        const prev = lastPersistedRef.current;
        const tasks: Promise<void>[] = [];
        const newLanguagesKey = [...languageIds].sort().join(",");
        if (newDefaultWeekKey !== prev.defaultWeekKey)
          tasks.push(saveWorkingHours(defaultWeek, syncActive));
        if (newLasKey !== prev.lasKey) tasks.push(saveLACoverage(las));

        const newExpertiseKey = JSON.stringify(expertiseLevels);
        if (
          newSpecKey !== prev.specialismsKey ||
          newExpertiseKey !== prev.specialismExpertiseLevelsKey
        )
          tasks.push(saveSpecialismsWithLevels(specIds, expertiseLevels));

        if (
          address !== prev.homeBaseAddress ||
          postalCode !== prev.homeBasePostalCode
        )
          tasks.push(saveHomeBase(address, postalCode));
        if (gender !== prev.selectedGender) tasks.push(saveGender(gender));
        if (newLanguagesKey !== prev.selectedLanguagesKey)
          tasks.push(saveLanguages(languageIds));
        if (
          works !== prev.worksForTrust ||
          (trust?.id ?? "") !== prev.trustId ||
          (trust?.name ?? "") !== prev.trustName ||
          team !== prev.trustTeam
        )
          tasks.push(saveTrustAffiliation(works, trust, team));
        if (
          travelH !== prev.maxTravelHours ||
          travelM !== prev.maxTravelMinutes
        )
          tasks.push(saveMaxTravelTime(travelH, travelM));
        if (
          emailPref !== prev.contactPreferenceEmail ||
          textPref !== prev.contactPreferenceText
        )
          tasks.push(saveContactPreferences(emailPref, textPref));

        if (tasks.length === 0) return;
        setAutoSaveStatus("saving");
        onAutoSave?.("saving");
        try {
          await Promise.all(tasks);
          lastPersistedRef.current = {
            defaultWeekKey: newDefaultWeekKey,
            lasKey: newLasKey,
            specialismsKey: newSpecKey,
            homeBaseAddress: address,
            homeBasePostalCode: postalCode,
            selectedGender: gender,
            selectedLanguagesKey: newLanguagesKey,
            worksForTrust: works,
            trustId: trust?.id ?? "",
            trustName: trust?.name ?? "",
            trustTeam: team,
            maxTravelHours: travelH,
            maxTravelMinutes: travelM,
            contactPreferenceEmail: emailPref,
            contactPreferenceText: textPref,
            specialismExpertiseLevelsKey: newExpertiseKey,
          };
          markSaved();
        } catch (error) {
          console.error("Auto-save failed:", error);
          setAutoSaveStatus("error");
          onAutoSave?.("error");
        }
      },
      AUTO_SAVE_DELAY,
    ),
    [
      saveWorkingHours,
      saveLACoverage,
      saveSpecialismsWithLevels,
      saveHomeBase,
      saveGender,
      saveLanguages,
      saveTrustAffiliation,
      saveMaxTravelTime,
      saveContactPreferences,
      markSaved,
    ],
  );

  // Fire auto-save whenever any saveable state drifts from persisted
  useEffect(() => {
    // Skip the initial render — state is already in sync with server
    if (initialLoadRef.current) {
      initialLoadRef.current = false;
      return;
    }
    // Show spinner immediately while debounce waits
    setAutoSaveStatus("saving");
    onAutoSave?.("saving");
    void debouncedAutoSave(
      formState.defaultWeek,
      formState.selectedLocalAuthorities,
      formState.selectedSpecialismIds,
      homeBaseAddress,
      homeBasePostalCode,
      calendarSyncActive,
      defaultWeekKey,
      lasKey,
      specialismsKey,
      selectedGender,
      selectedLanguageIds,
      worksForTrust,
      selectedTrust,
      trustTeam,
      maxTravelHours,
      maxTravelMinutes,
      contactPreferenceEmail,
      contactPreferenceText,
      specialismExpertiseLevels,
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    defaultWeekKey,
    lasKey,
    specialismsKey,
    homeBaseAddress,
    homeBasePostalCode,
    selectedGender,
    selectedLanguageIds,
    worksForTrust,
    selectedTrust,
    trustTeam,
    maxTravelHours,
    maxTravelMinutes,
    contactPreferenceEmail,
    contactPreferenceText,
    specialismExpertiseLevels,
  ]);

  // Cleanup debounce on unmount
  useEffect(
    () => () => {
      debouncedAutoSave.cancel();
    },
    [debouncedAutoSave],
  );

  // Expose a flush function so the parent can trigger an immediate save
  useEffect(() => {
    if (saveNowRef) {
      saveNowRef.current = async () => {
        await debouncedAutoSave.flush();
      };
    }
    return () => {
      if (saveNowRef) {
        saveNowRef.current = null;
      }
    };
  }, [saveNowRef, debouncedAutoSave]);

  // Use professional address for home base
  const handleUseProfileAddress = () => {
    const profileAddress = user?.address;
    if (profileAddress) {
      setHomeBaseAddress(profileAddress.address ?? "");
      setHomeBasePostalCode(profileAddress.postalCode ?? "");
    }
  };

  // Get specialism names for display
  const getSpecialismName = (id: string): string => {
    const specialism = allSpecialisms?.find((s) => s.id === id);
    return specialism?.specialismName ?? "Unknown";
  };

  // Whether an imported calendar is one of the availability layers.
  const [calendarSyncActive, setCalendarSyncActive] = useState(false);

  // Calendar sync management dialog
  const [calendarManageOpen, setCalendarManageOpen] = useState(false);
  const [calendarSummary, setCalendarSummary] = useState<CalendarSyncSummary>({
    connections: [],
    enabled: false,
  });

  // S12 expiry warning - get from accreditations
  const today = dayjs();
  const s12Accreditation = user?.accreditations.find(isSection12Accreditation);
  const expiryDate = s12Accreditation?.expiryDate
    ? dayjs(s12Accreditation.expiryDate)
    : null;
  const isExpired = expiryDate ? expiryDate.isBefore(today, "day") : false;
  const daysUntilExpiry = expiryDate ? expiryDate.diff(today, "day") : null;
  const showExpiryWarning =
    expiryDate &&
    (isExpired || (daysUntilExpiry !== null && daysUntilExpiry <= 30));

  // Active section state (driven by setup area card clicks)
  const [activeSection, setActiveSection] = useState("schedule");

  const effectiveActiveSection = externalActiveSection ?? activeSection;

  const changeActiveSection = useCallback(
    (section: string) => {
      setActiveSection(section);
      onActiveSectionChange?.(section);
    },
    [onActiveSectionChange],
  );

  // Setup score computation
  const setupAreas = useMemo(
    () =>
      buildSetupAreas({
        hasPhoneNumber:
          user?.section12PhoneNumber != null &&
          user.section12PhoneNumber.length > 0,
        hasWorkingHours: formState.defaultWeek.length > 0,
        hasHomeBase: Boolean(homeBaseAddress && homeBasePostalCode),
        hasLaCoverage: formState.selectedLocalAuthorities.length > 0,
        hasSpecialisms: formState.selectedSpecialismIds.length > 0,
        calendarSyncActive,
        hasAboutInfo:
          selectedGender !== "" ||
          selectedLanguageIds.length > 0 ||
          worksForTrust,
      }),
    [
      user?.section12PhoneNumber,
      formState.defaultWeek.length,
      homeBaseAddress,
      homeBasePostalCode,
      formState.selectedLocalAuthorities.length,
      formState.selectedSpecialismIds.length,
      calendarSyncActive,
      selectedGender,
      selectedLanguageIds.length,
      worksForTrust,
    ],
  );

  const setupComplete = useMemo(
    () => setupAreas.filter((a) => !a.bonus).every((a) => a.complete),
    [setupAreas],
  );

  if (specialismsLoading) {
    return (
      <Box
        sx={{
          height: "100%",
          display: "grid",
          justifyItems: "center",
          alignItems: "center",
        }}
      >
        <LoadingSpinner label="Loading..." />
      </Box>
    );
  }

  return (
    <>
      {!embedded && showExpiryWarning && (
        <Box sx={{ px: 2, pt: 2 }}>
          <Banner
            bannerType={isExpired ? BannerList.ERROR : BannerList.WARNING}
            title={
              isExpired
                ? "Section 12 Accreditation Expired"
                : "Section 12 Accreditation Expiring Soon"
            }
            body={`Your Section 12 accreditation ${isExpired ? "expired" : "expires"} on ${expiryDate.format("D MMMM YYYY")}. Please update your details.`}
          />
        </Box>
      )}
      {/* Page header: title + back link + auto-save status — hidden in embedded mode */}
      {!embedded && (
        <Container
          sx={{
            pt: 2,
            pb: 1,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          {setupComplete ? (
            <Typography variant="h5" component="h1">
              S12 Profile
            </Typography>
          ) : (
            <Box />
          )}

          <Box
            sx={{ display: "flex", alignItems: "center", gap: 2 }}
            data-testid="s12-profile-header-actions"
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                minHeight: 24,
              }}
            >
              <Fade in={displayedAutoSaveStatus === "saving"} unmountOnExit>
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  <CircularProgress size={14} />
                  <Typography
                    variant="caption"
                    sx={{
                      color: "text.secondary",
                    }}
                  >
                    Saving…
                  </Typography>
                </Box>
              </Fade>
              <Fade in={displayedAutoSaveStatus === "saved"} unmountOnExit>
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  <Check fontSize="small" color="success" />
                  <Typography
                    variant="caption"
                    sx={{
                      color: "success.main",
                    }}
                  >
                    Saved
                  </Typography>
                </Box>
              </Fade>
              <Fade in={displayedAutoSaveStatus === "error"} unmountOnExit>
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  <ErrorOutline fontSize="small" color="error" />
                  <Typography
                    variant="caption"
                    sx={{
                      color: "error.main",
                    }}
                  >
                    Save failed — retrying…
                  </Typography>
                </Box>
              </Fade>
            </Box>
            <Button
              variant="text"
              label="Back to Profile"
              startIcon={ButtonIcon.arrowBack}
              onClick={() => {
                navigate(routeFns.userProfilePage(userId));
              }}
              testId="s12-back-to-profile-btn"
            />
          </Box>
        </Container>
      )}
      <Container sx={{ pb: 4 }}>
        {!setupComplete ? (
          <SetupScoreCards
            areas={setupAreas}
            selectedSection={effectiveActiveSection}
            onAreaClick={changeActiveSection}
          />
        ) : (
          // Embedded, the host page's own tabs move between the sections.
          !embedded && (
            <Paper variant="outlined" sx={{ mb: 3 }}>
              <Tabs
                value={effectiveActiveSection}
                onChange={(_, value: string) => {
                  changeActiveSection(value);
                }}
                variant="fullWidth"
              >
                <Tab label="Schedule" value="schedule" />
                <Tab label="Location" value="location" />
                <Tab label="Specialisms" value="specialisms" />
                <Tab label="About" value="about" />
                <Tab label="Contact" value="contact" />
              </Tabs>
            </Paper>
          )
        )}

        {/* About Section */}
        {effectiveActiveSection === "about" && (
          <>
            <Paper sx={{ p: 3, mb: 3 }} data-testid="section12-gender-section">
              <Typography variant="h6" gutterBottom>
                Gender
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Optionally set your gender so AMHPs can see it when searching
                for available doctors
              </Typography>
              <FormControl sx={{ minWidth: 240 }} size="small">
                <InputLabel id="s12-gender-label">Gender</InputLabel>
                <Select
                  labelId="s12-gender-label"
                  value={selectedGender}
                  label="Gender"
                  onChange={(e) => {
                    setSelectedGender(e.target.value as Section12Gender | "");
                  }}
                  data-testid="s12-gender-select"
                >
                  <MenuItem value="">
                    <em>Not set</em>
                  </MenuItem>
                  {(
                    Object.entries(SECTION_12_GENDER_LABELS) as [
                      Section12Gender,
                      string,
                    ][]
                  ).map(([value, label]) => (
                    <MenuItem key={value} value={value}>
                      {label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Paper>

            <Paper
              sx={{ p: 3, mb: 3 }}
              data-testid="section12-languages-section"
            >
              <Typography variant="h6" gutterBottom>
                Languages
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Optionally set the languages you speak so AMHPs can see them
                when searching for available doctors
              </Typography>
              <Autocomplete
                multiple
                options={allLanguages ?? []}
                value={selectedLanguages}
                onChange={(_event, newValue) => {
                  setSelectedLanguageIds(
                    newValue.map((language) => language.id),
                  );
                }}
                getOptionLabel={(option) => option.languageName}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                loading={languagesLoading}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder={
                      selectedLanguageIds.length === 0
                        ? "Search languages..."
                        : ""
                    }
                    size="small"
                  />
                )}
                renderValue={(value, getItemProps) =>
                  value.map((option, index) => {
                    const { key, ...itemProps } = getItemProps({ index });
                    return (
                      <Chip
                        key={key}
                        {...itemProps}
                        label={option.languageName}
                        size="small"
                      />
                    );
                  })
                }
                size="small"
                sx={{ maxWidth: 400 }}
                data-testid="s12-languages-autocomplete"
              />
            </Paper>

            {/* Trust Affiliation */}
            <Paper sx={{ p: 3, mb: 3 }} data-testid="section12-trust-section">
              <Typography variant="h6" gutterBottom>
                Trust Affiliation
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Outside of your independent role, do you work for a trust?
              </Typography>
              <RadioGroup
                row
                value={worksForTrust ? "yes" : "no"}
                onChange={(e) => {
                  const works = e.target.value === "yes";
                  setWorksForTrust(works);
                  if (!works) {
                    setSelectedTrust(null);
                    setTrustTeam("");
                  }
                }}
                data-testid="s12-works-for-trust-radio"
              >
                <FormControlLabel value="yes" control={<Radio />} label="Yes" />
                <FormControlLabel value="no" control={<Radio />} label="No" />
              </RadioGroup>

              {worksForTrust && (
                <Box
                  sx={{
                    mt: 2,
                    display: "flex",
                    flexDirection: "column",
                    gap: 2,
                  }}
                >
                  <TrustPicker
                    value={selectedTrust}
                    onChange={setSelectedTrust}
                    allowNotListed
                    label="Select Trust"
                    testId="s12-trust-picker"
                  />
                  <TextField
                    label="Team within this trust"
                    value={trustTeam}
                    onChange={(e) => {
                      setTrustTeam(e.target.value);
                    }}
                    size="small"
                    placeholder="e.g. Community Mental Health Team"
                    sx={{ maxWidth: 400 }}
                    data-testid="s12-trust-team-input"
                  />
                </Box>
              )}
            </Paper>
          </>
        )}

        {/* Contact Section */}
        {effectiveActiveSection === "contact" && (
          <>
            <PhoneVerificationSection userId={userId} />
            <Paper
              sx={{ p: 3, mb: 3 }}
              data-testid="section12-contact-preferences-section"
            >
              <Typography variant="h6" gutterBottom>
                Contact Preferences
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                How would you like AMHPs to contact you? Phone call is
                mandatory.
              </Typography>
              <FormGroup>
                <FormControlLabel
                  control={<Checkbox checked disabled />}
                  label="Phone Call (mandatory)"
                  data-testid="s12-contact-phone"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={contactPreferenceEmail}
                      onChange={(e) => {
                        setContactPreferenceEmail(e.target.checked);
                      }}
                    />
                  }
                  label="Email"
                  data-testid="s12-contact-email"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={contactPreferenceText}
                      onChange={(e) => {
                        setContactPreferenceText(e.target.checked);
                      }}
                    />
                  }
                  label="Text"
                  data-testid="s12-contact-text"
                />
              </FormGroup>
            </Paper>
          </>
        )}

        {/* Schedule Section: Working Hours + Calendar Sync + Availability */}
        {effectiveActiveSection === "schedule" && (
          <>
            <DefaultWorkingHoursSection
              defaultWeek={formState.defaultWeek}
              onDefaultWeekChange={(defaultWeek) => {
                setFormState((prev) => ({ ...prev, defaultWeek }));
              }}
              calendarSyncActive={calendarSyncActive}
              calendarSummary={calendarSummary}
              onManageCalendar={() => {
                setCalendarManageOpen(true);
              }}
            />
            {formState.defaultWeek.length > 0 && (
              <Paper
                sx={{ p: 3, mb: 3 }}
                data-testid="section12-upcoming-availability-section"
              >
                <Typography variant="h6" gutterBottom>
                  Your Upcoming Availability
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    color: "text.secondary",
                    mb: 2,
                  }}
                >
                  This is what AMHPs will see when looking for available
                  doctors. Paint over a day to change just that day.
                </Typography>
                <UpcomingAvailabilityCalendar
                  now={now}
                  defaultWeek={formState.defaultWeek}
                  overrides={overrides}
                  saveOverrides={async (request) => {
                    await api.section12.setUserAvailabilityOverrides(
                      userId,
                      request,
                    );
                  }}
                  deleteOverrides={async (range) => {
                    await api.section12.deleteUserAvailabilityOverrides(
                      userId,
                      range,
                    );
                  }}
                  onSaved={fetchOverrides}
                  onAutoSaveStatus={handleCalendarAutoSaveStatus}
                  flushRef={calendarFlushRef}
                />
              </Paper>
            )}
          </>
        )}

        {/* Hidden CalendarSyncSection to load data for summary strip */}
        <Box sx={{ display: "none" }}>
          <CalendarSyncSection
            userId={userId}
            onSyncStatusChange={setCalendarSyncActive}
            onSyncComplete={() => {
              void fetchOverrides();
            }}
            embedded
            onSummaryChange={setCalendarSummary}
          />
        </Box>

        {/* Calendar Sync Management Dialog */}
        <Dialog
          open={calendarManageOpen}
          onClose={() => {
            setCalendarManageOpen(false);
          }}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            Sync calendar
            <IconButton
              size="small"
              aria-label="Close"
              onClick={() => {
                setCalendarManageOpen(false);
              }}
              data-testid="calendar-sync-close-icon"
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent>
            <CalendarSyncSection
              userId={userId}
              onSyncStatusChange={setCalendarSyncActive}
              onSyncComplete={() => {
                void fetchOverrides();
              }}
              embedded
              onSummaryChange={setCalendarSummary}
            />
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button
              variant="outlined"
              label="Close"
              onClick={() => {
                setCalendarManageOpen(false);
              }}
              testId="calendar-sync-close-btn"
            />
          </DialogActions>
        </Dialog>

        {/* Location & Coverage Section */}
        {effectiveActiveSection === "location" && (
          <>
            {/* Home Base Location */}
            <Paper
              sx={{ p: 3, mb: 3 }}
              data-testid="section12-home-base-section"
            >
              <Typography variant="h6" gutterBottom>
                Home Base Location
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: "text.secondary",
                  mb: 2,
                }}
              >
                Set your home base location so AMHPs can estimate travel times
                to assessment locations
              </Typography>

              <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                <AddressField
                  name="homeBaseAddress"
                  label="Home Base Address"
                  hideConfirmation
                  googleMapsApiKey={config.googleMapsApiKey}
                  addressCheckBoxLabel=""
                  value={{
                    address: homeBaseAddress,
                    postalCode: homeBasePostalCode,
                  }}
                  onChange={(value: {
                    address: string;
                    postalCode: string;
                  }) => {
                    setHomeBaseAddress(value.address);
                    setHomeBasePostalCode(value.postalCode);
                  }}
                  testId="home-base"
                />
                {user?.address && (
                  <Button
                    variant="outlined"
                    label="Use my professional address"
                    onClick={handleUseProfileAddress}
                    testId="use-profile-address-btn"
                    sx={{ alignSelf: "flex-start" }}
                  />
                )}
              </Box>
            </Paper>

            {/* Maximum Travel Distance */}
            <Paper
              sx={{ p: 3, mb: 3 }}
              data-testid="section12-max-travel-section"
            >
              <Typography variant="h6" gutterBottom>
                Maximum Travel Distance
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Optionally specify how far you are willing to travel for
                assessments. Doctors who are further away than they are willing
                to travel will still appear in search results, but will not be
                shown as &quot;matching&quot;.
              </Typography>
              <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
                <TextField
                  label="Hours"
                  type="number"
                  value={maxTravelHours}
                  onChange={(e) => {
                    const val = e.target.value;
                    if (
                      val === "" ||
                      (parseInt(val, 10) >= 0 && parseInt(val, 10) <= 24)
                    ) {
                      setMaxTravelHours(val);
                    }
                  }}
                  size="small"
                  sx={{ width: 100 }}
                  slotProps={{ htmlInput: { min: 0, max: 24 } }}
                  data-testid="s12-max-travel-hours"
                />
                <TextField
                  label="Minutes"
                  type="number"
                  value={maxTravelMinutes}
                  onChange={(e) => {
                    const val = e.target.value;
                    if (
                      val === "" ||
                      (parseInt(val, 10) >= 0 && parseInt(val, 10) <= 59)
                    ) {
                      setMaxTravelMinutes(val);
                    }
                  }}
                  size="small"
                  sx={{ width: 100 }}
                  slotProps={{ htmlInput: { min: 0, max: 59 } }}
                  data-testid="s12-max-travel-minutes"
                />
              </Box>
              {(maxTravelHours || maxTravelMinutes) && (
                <Button
                  variant="text"
                  label="Clear travel preference"
                  onClick={() => {
                    setMaxTravelHours("");
                    setMaxTravelMinutes("");
                  }}
                  small
                  sx={{ mt: 1 }}
                  testId="s12-max-travel-clear"
                />
              )}
            </Paper>

            {/* Local Authority Coverage */}
            <Paper sx={{ p: 3, mb: 3 }} data-testid="section12-la-section">
              <Typography variant="h6" gutterBottom>
                Local Authority Coverage
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: "text.secondary",
                  mb: 2,
                }}
              >
                Select the local authorities where you are available to work
              </Typography>

              <Box sx={{ mb: 2 }}>
                <Typeahead
                  resetKey={formState.selectedLocalAuthorities
                    .map((la) => la.locationId)
                    .join("-")}
                  label="Add Local Authority"
                  enableFuse={false}
                  options={availableLocalAuthorities}
                  inputValue={laInputValue}
                  optionsKey="name"
                  name="localAuthority"
                  getOptionLabel={(
                    option: (typeof availableLocalAuthorities)[0] | string,
                  ) => {
                    if (typeof option === "string") {
                      return option;
                    }
                    return option.name;
                  }}
                  onInputChange={(value: string | null) => {
                    const stringValue = value ?? "";
                    const matchedLa = availableLocalAuthorities.find(
                      (la) => la.name === stringValue,
                    );
                    if (matchedLa) {
                      handleAddLocalAuthority(matchedLa);
                      setTimeout(() => {
                        setLaInputValue("");
                      }, 0);
                    } else {
                      setLaInputValue(stringValue);
                    }
                  }}
                  data-testid="section12-la-typeahead"
                />
              </Box>

              <Stack
                direction="row"
                spacing={1}
                useFlexGap
                data-testid="section12-la-chips"
                sx={{
                  flexWrap: "wrap",
                  border: "1px solid #ccc",
                  borderRadius: "4px",
                  padding: "1em",
                  minHeight: "4em",
                }}
              >
                {formState.selectedLocalAuthorities.length === 0 ? (
                  <Typography
                    variant="body2"
                    sx={{
                      color: "text.secondary",
                    }}
                  >
                    No local authorities selected
                  </Typography>
                ) : (
                  formState.selectedLocalAuthorities.map((la) => (
                    <Chip
                      key={la.locationId}
                      label={la.locationName}
                      variant="outlined"
                      onDelete={() => {
                        handleRemoveLocalAuthority(la.locationId);
                      }}
                      data-testid={`section12-la-chip-${la.locationId}`}
                    />
                  ))
                )}
              </Stack>
            </Paper>
          </>
        )}

        {/* Specialisms Section */}
        {effectiveActiveSection === "specialisms" && (
          <Paper
            sx={{ p: 3, mb: 3 }}
            data-testid="section12-specialisms-section"
          >
            <Typography variant="h6" gutterBottom>
              Specialisms
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: "text.secondary",
                mb: 2,
              }}
            >
              Select your areas of expertise
            </Typography>

            <Box sx={{ mb: 2 }}>
              <Typeahead
                resetKey={formState.selectedSpecialismIds.join("-")}
                label="Add Specialism"
                enableFuse={false}
                options={availableSpecialisms}
                inputValue={specialismInputValue}
                optionsKey="specialismName"
                name="specialism"
                getOptionLabel={(option: Section12Specialism | string) => {
                  if (typeof option === "string") {
                    return option;
                  }
                  return option.specialismName;
                }}
                onInputChange={(value: string | null) => {
                  const stringValue = value ?? "";
                  const matchedSpecialism = availableSpecialisms.find(
                    (s) => s.specialismName === stringValue,
                  );
                  if (matchedSpecialism) {
                    handleAddSpecialism(matchedSpecialism);
                    setTimeout(() => {
                      setSpecialismInputValue("");
                    }, 0);
                  } else {
                    setSpecialismInputValue(stringValue);
                  }
                }}
                data-testid="section12-specialism-typeahead"
              />
            </Box>

            <Stack
              direction="column"
              spacing={1}
              sx={{
                flexWrap: "wrap",
                border: "1px solid #ccc",
                borderRadius: "4px",
                padding: "1em",
                minHeight: "4em",
              }}
            >
              {formState.selectedSpecialismIds.length === 0 ? (
                <Typography
                  variant="body2"
                  sx={{
                    color: "text.secondary",
                  }}
                >
                  No specialisms selected
                </Typography>
              ) : (
                formState.selectedSpecialismIds.map((id) => (
                  <Box
                    key={id}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      flexWrap: "wrap",
                    }}
                  >
                    <Chip
                      label={getSpecialismName(id)}
                      variant="outlined"
                      onDelete={() => {
                        handleRemoveSpecialism(id);
                        setSpecialismExpertiseLevels(
                          ({ [id]: _removed, ...rest }) => rest,
                        );
                      }}
                      data-testid={`section12-specialism-chip-${id}`}
                    />
                    <FormControl size="small" sx={{ minWidth: 160 }}>
                      <InputLabel id={`expertise-label-${id}`}>
                        Expertise Level
                      </InputLabel>
                      <Select<Section12ExpertiseLevel | "">
                        labelId={`expertise-label-${id}`}
                        value={specialismExpertiseLevels[id] ?? ""}
                        label="Expertise Level"
                        onChange={(e) => {
                          const value =
                            e.target.value === ""
                              ? null
                              : (e.target.value as Section12ExpertiseLevel);
                          setSpecialismExpertiseLevels((prev) => ({
                            ...prev,
                            [id]: value,
                          }));
                        }}
                        data-testid={`section12-expertise-select-${id}`}
                      >
                        <MenuItem value="">
                          <em>Not set</em>
                        </MenuItem>
                        {(
                          Object.entries(SECTION_12_EXPERTISE_LEVEL_LABELS) as [
                            Section12ExpertiseLevel,
                            string,
                          ][]
                        ).map(([value, label]) => (
                          <MenuItem key={value} value={value}>
                            {label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Box>
                ))
              )}
            </Stack>
          </Paper>
        )}
      </Container>
    </>
  );
}
